import type {Evaluator, Version} from "@cm/graph-v3"
import {CircularRef, graphNode, versionChain} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {ObjectNode} from "#template/nodes/object/object-node"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import {objectNodeParameters} from "#template/nodes/object/validation"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {curveLike, isCurveLikeNode} from "#template/nodes/object/curve/fundamental"
import {objectLike} from "#template/nodes/object/fundamental"
import {NodesInput} from "#template/fundamental/input/nodes-input"
import type {MeshCurve} from "#template/nodes/object/curve/mesh-curve"
import {completeCurveDataForControlPointsDefinedCurve} from "#template/nodes/object/curve/mesh-curve"
import type {CurveLikeNode, ObjectLikeNode} from "#template/nodes/types"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {SceneNodes} from "#template/interfaces/scene-object"
import type {CurveObjectData, ObjectData} from "#template/interfaces/object-data"
import {isCurveObjectData} from "#template/interfaces/object-data"
import {SampleBridgeCurve} from "#template/runtime-graph/nodes/sample-bridge-curve"
import {mergeBounds, transformBounds} from "#template/utils/scene-geometry-utils"
import type {Matrix4} from "@cm/math"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {RawBounds} from "#template/geometry-processing/bounds"
import type {CompleteCurveData} from "#template/interfaces/curve-data"
import {hashObject} from "@cm/utils"
import {visitNone} from "#template/template-node/visit-helpers"

type SeamCurve = {
    id: string
    transform: Matrix4
    meshCurveControl: SceneNodes.MeshCurveControl
    completeCurveData: CompleteCurveData
}

// the sampler repeats a closed curve's first sample at its end, so a line through the samples closes; a seam puts an item
// at every sample and closes its bridges itself, so the repeat would double the first item and add a bridge of no length
export function withoutClosingSample(completeCurveData: CompleteCurveData, closed: boolean): CompleteCurveData {
    const {reified} = completeCurveData
    if (!closed || !reified || reified.segments.length < 2) return completeCurveData
    const count = reified.segments.length - 1
    return {
        ...completeCurveData,
        reified: {
            contentHash: hashObject([reified.contentHash, "withoutClosingSample"]),
            points: reified.points.slice(0, count * 3),
            normals: reified.normals.slice(0, count * 3),
            tangents: reified.tangents.slice(0, count * 3),
            segments: reified.segments.slice(0, count),
            scales: reified.scales.slice(0, count),
            sectionIds: reified.sectionIds.slice(0, count),
        },
    }
}

function mapper(
    curveScope: GraphBuilderScope,
    curveData: CurveObjectData,
    ctx: BuilderInlet<{
        allowScaling: boolean
        segmentLength: number
        offsetLength: number | undefined
        bridgeMeshes: SceneNodes.Mesh[]
        bounds: RawBounds[]
    }>,
    curveIndex: number,
): BuilderInlet<{
    seamCurve: SeamCurve
    bridgeCurve: SeamCurve | null
} | null> {
    if (curveData.visible === false) return null

    const completeCurveDataForSeamCurve = curveScope.pureLambda(
        curveScope.tuple(
            completeCurveDataForControlPointsDefinedCurve(
                curveScope,
                curveScope.get(curveData, "controlPoints"),
                curveScope.get(curveData, "closed"),
                curveScope.get(ctx, "segmentLength"),
                curveScope.get(ctx, "allowScaling"),
                curveScope.get(ctx, "offsetLength"),
            ),
            curveScope.get(curveData, "closed"),
        ),
        ([completeCurveData, closed]) => withoutClosingSample(completeCurveData, closed),
        "seamCurveData",
    )

    const meshCurveControl = curveScope.pureLambda(
        curveData,
        (curveData) => {
            const meshCurveControl = [...curveData.preDisplayList, ...curveData.displayList].filter(SceneNodes.MeshCurveControl.is)
            if (meshCurveControl.length !== 1) throw new Error("MeshCurveControl not found")
            return meshCurveControl[0]
        },
        "meshCurveControl",
    )

    const seamCurve = curveScope.struct<SeamCurve>("SeamCurve", {
        id: `${curveIndex}`,
        transform: curveScope.get(curveData, "matrix"),
        meshCurveControl,
        completeCurveData: completeCurveDataForSeamCurve,
    })

    const bridgeMeshes = curveScope.get(ctx, "bridgeMeshes")

    const sampledBridgeCurveInput = {
        inputCurvePoints: curveScope.pureLambda(
            curveScope.tuple(curveData, bridgeMeshes, completeCurveDataForSeamCurve),
            ([curveData, bridgeMeshes, completeCurveData]) => {
                if (bridgeMeshes.length === 0 || curveData.visible === false) return null
                return completeCurveData.reified
            },
            "bridgeCurvePoints",
        ),
        holeBounds: curveScope.get(ctx, "bounds"),
        bridgeBounds: curveScope.pureLambda(
            bridgeMeshes,
            (bridgeMeshes) => {
                const bounds = mergeBounds(bridgeMeshes.map((mesh) => transformBounds(mesh.completeMeshData.reified.bounds, mesh.transform)))
                return bounds
            },
            "bridgeBounds",
        ),
        closed: curveScope.pureLambda(
            curveData,
            (curveData) => {
                return curveData.closed && curveData.controlPoints.length > 2
            },
            "bridgeCurveClosed",
        ),
    }

    const {outputCurvePoints} = curveScope.node(SampleBridgeCurve, sampledBridgeCurveInput)

    const completeCurveDataForBridgeCurve = curveScope.pureLambda(
        curveScope.tuple(
            outputCurvePoints,
            completeCurveDataForSeamCurve,
            sampledBridgeCurveInput.holeBounds,
            sampledBridgeCurveInput.bridgeBounds,
            sampledBridgeCurveInput.closed,
        ),
        ([outputCurvePoints, completeCurveDataForSeamCurve, holeBounds, bridgeBounds, close]): CompleteCurveData => ({
            reified:
                outputCurvePoints === null
                    ? null
                    : {
                          contentHash: hashObject([completeCurveDataForSeamCurve.reified?.contentHash, holeBounds, bridgeBounds, close]),
                          ...outputCurvePoints,
                      },
            abstract: undefined,
        }),
        "completeCurveDataForBridgeCurve",
    )

    const bridgeCurve = curveScope.pureLambda<[CompleteCurveData, SeamCurve], SeamCurve | null>(
        curveScope.tuple(completeCurveDataForBridgeCurve, seamCurve),
        ([completeCurveDataForBridgeCurve, seamCurve]) => {
            if (!completeCurveDataForBridgeCurve.reified) return null
            return {...seamCurve, completeCurveData: completeCurveDataForBridgeCurve}
        },
        "bridgeCurve",
    )

    return curveScope.struct("Result", {
        seamCurve,
        bridgeCurve,
    })
}

export type SeamParameters = {
    name: string
    item: ObjectLikeNode | null
    bridge?: ObjectLikeNode
    repeatSize?: number | ObjectLikeNode
    repeatSizeFactor?: number
    offsetSize?: number | ObjectLikeNode
    offsetFactor?: number
    curves: (CurveLikeNode | NodesInput)[]
    allowScaling: boolean
} & ObjectNodeParameters

type V0 = {
    name: string
    item: ObjectLikeNode | null
    bridge?: ObjectLikeNode
    repeatSize?: number | ObjectLikeNode
    repeatSizeFactor?: number
    offsetSize?: number | ObjectLikeNode
    offsetFactor?: number
    curve: MeshCurve | null
    allowScaling: boolean
}

type V1 = Omit<V0, "curve"> & {curves: (CurveLikeNode | NodesInput)[]}

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => {
        const {curve, ...rest} = parameters
        if (curve instanceof CircularRef) throw new Error("Cannot resolve circular references when going from v0 to v1 in seam node")
        return {
            curves: curve ? [curve] : [],
            ...rest,
        }
    },
}

const seamDefinition: ObjectNodeDefinition<SeamParameters> = {
    nodeClass: "Seam",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                item: z.nullable(objectLike),
                bridge: z.optional(objectLike),
                repeatSize: z.optional(z.union([z.number(), objectLike])),
                repeatSizeFactor: z.optional(z.number()),
                offsetSize: z.optional(z.union([z.number(), objectLike])),
                offsetFactor: z.optional(z.number()),
                curves: z.array(z.union([curveLike, graphNode(NodesInput)])),
                allowScaling: z.boolean(),
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0]),
}

@registerTemplateGraphNode
export class Seam extends ObjectNode<SeamParameters> {
    public override get definition() {
        return seamDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<SeamParameters> {
        if (this.parameters.item === null || this.parameters.curves.length === 0) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<SeamParameters> {
        const {item, bridge, curves, repeatSize, allowScaling} = this.parameters

        if (item === null || curves.length === 0) return skipped

        const {evaluator} = context

        const scope = evaluator.getScope(this)

        const [objectData, objectDataInvalid] = scope.branch(evaluator.evaluate(item))
        const processedObjectData = scope.pureLambda(
            objectData,
            ({preDisplayList, displayList, matrix}) => {
                const inverseTransform = matrix.inverse()

                const meshes = displayList.filter(SceneNodes.Mesh.is).map<SceneNodes.Mesh>(({id, transform, ...mesh}) => ({
                    ...mesh,
                    id: `${id}-seamItem`,
                    transform: inverseTransform.multiply(transform),
                    receiveRealtimeShadows: false,
                }))

                const filteredPreDisplayList = preDisplayList.filter((x) => {
                    if (SceneNodes.PreloadMaterial.is(x)) return true
                    else if (SceneNodes.DataObjectReference.is(x)) return meshes.some((mesh) => mesh.path === x.path)
                    return false
                })

                const bounds = meshes.map((mesh) => transformBounds(mesh.completeMeshData.reified.bounds, mesh.transform))

                return {meshes, bounds, preDisplayList: filteredPreDisplayList}
            },
            "processedObjectData",
        )
        const meshes = scope.get(processedObjectData, "meshes")
        const bounds = scope.get(processedObjectData, "bounds")
        const preDisplayList = scope.get(processedObjectData, "preDisplayList")

        const processedBridge = scope.pureLambda(
            evaluator.evaluate(bridge ?? null),
            (bridge) => {
                if (!bridge) return {meshes: [], preDisplayList: []}
                const {preDisplayList, displayList, matrix} = bridge
                const inverseTransform = matrix.inverse()

                const bridgeMeshes = [...preDisplayList, ...displayList].filter(SceneNodes.Mesh.is).map<SceneNodes.Mesh>(({id, transform, ...mesh}) => ({
                    ...mesh,
                    id: `${id}-origin`,
                    transform: inverseTransform.multiply(transform),
                }))

                const filteredPreDisplayList = preDisplayList.filter((x) => {
                    if (SceneNodes.PreloadMaterial.is(x)) return true
                    else if (SceneNodes.DataObjectReference.is(x)) return bridgeMeshes.some((mesh) => mesh.path === x.path)
                    return false
                })

                return {meshes: bridgeMeshes, preDisplayList: filteredPreDisplayList}
            },
            "processedBridge",
        )

        const bridgeMeshes = scope.get(processedBridge, "meshes")
        const bridgePreDisplayList = scope.get(processedBridge, "preDisplayList")

        const repeat = typeof repeatSize === "number" ? repeatSize : evaluator.evaluate(repeatSize ?? null)
        const processedRepeat = scope.pureLambda(
            scope.tuple(meshes, repeat, this.parameters.repeatSizeFactor),
            ([meshes, repeat, repeatSizeFactor]) => {
                const factor = repeatSizeFactor ?? 1
                if (typeof repeat === "number") return {segmentLength: repeat * factor, preDisplayList: []}

                const repeatMeshes = (() => {
                    if (repeat) {
                        const inverseTransform = repeat.matrix.inverse()
                        return [...repeat.preDisplayList, ...repeat.displayList]
                            .filter(SceneNodes.Mesh.is)
                            .map<SceneNodes.Mesh>((mesh) => ({...mesh, transform: inverseTransform.multiply(mesh.transform)}))
                    } else return meshes
                })()

                const bounds = mergeBounds(repeatMeshes.map((mesh) => transformBounds(mesh.completeMeshData.reified.bounds, mesh.transform)))
                const size = Math.max(Math.abs(bounds.aabb[0][2]), Math.abs(bounds.aabb[1][2])) * 2

                const filteredPreDisplayList = repeat
                    ? repeat.preDisplayList.filter((x) => {
                          if (SceneNodes.DataObjectReference.is(x) && x.origin.type === "mesh") return repeatMeshes.some((mesh) => mesh.path === x.path)
                          return false
                      })
                    : []

                return {segmentLength: size * factor, preDisplayList: filteredPreDisplayList}
            },
            "processedRepeat",
        )

        const segmentLength = scope.get(processedRepeat, "segmentLength")
        const segmentPreDisplayList = scope.get(processedRepeat, "preDisplayList")

        const offset = typeof this.parameters.offsetSize === "number" ? this.parameters.offsetSize : evaluator.evaluate(this.parameters.offsetSize ?? null)
        const processedOffset = scope.pureLambda(
            scope.tuple(offset, this.parameters.offsetFactor),
            ([offset, offsetFactor]) => {
                const factor = offsetFactor ?? 1
                if (typeof offset === "number") return {offsetLength: offset * factor, preDisplayList: []}
                if (!offset) return {offsetLength: undefined, preDisplayList: []}

                const offsetMeshes = (() => {
                    const inverseTransform = offset.matrix.inverse()
                    return [...offset.preDisplayList, ...offset.displayList]
                        .filter(SceneNodes.Mesh.is)
                        .map<SceneNodes.Mesh>((mesh) => ({...mesh, transform: inverseTransform.multiply(mesh.transform)}))
                })()

                const bounds = mergeBounds(offsetMeshes.map((mesh) => transformBounds(mesh.completeMeshData.reified.bounds, mesh.transform)))
                const size = Math.max(Math.abs(bounds.aabb[0][2]), Math.abs(bounds.aabb[1][2])) * 2

                const filteredPreDisplayList = offset.preDisplayList.filter((x) => {
                    if (SceneNodes.DataObjectReference.is(x) && x.origin.type === "mesh") return offsetMeshes.some((mesh) => mesh.path === x.path)
                    return false
                })

                return {offsetLength: size * factor, preDisplayList: filteredPreDisplayList}
            },
            "processedOffset",
        )

        const offsetLength = scope.get(processedOffset, "offsetLength")
        const offsetPreDisplayList = scope.get(processedOffset, "preDisplayList")

        const curveData = scope.filterInvalid(
            scope.merge(
                curves.map((curveNode) => {
                    if (isCurveLikeNode(curveNode)) {
                        return scope.list([evaluator.evaluate(curveNode)])
                    } else {
                        return scope.pureLambda(
                            curveNode.evaluate(scope, evaluator),
                            (evaluatedNodes) => {
                                return evaluatedNodes.map((evaluatedNode) => {
                                    if (evaluatedNode.type === "Mesh" || evaluatedNode.type === "Curve") {
                                        const objectData = evaluatedNode.value as ObjectData | null
                                        if (objectData && isCurveObjectData(objectData)) return objectData as CurveObjectData
                                    }

                                    return null
                                })
                            },
                            "curveData",
                        )
                    }
                }),
            ),
        )

        const seamCurveData = scope.filterInvalid(
            scope.map(curveData, mapper, scope.struct("Context", {allowScaling, segmentLength, offsetLength, bridgeMeshes, bounds})),
        )

        const [, hasNoSeamCurves] = scope.branch(
            scope.pureLambda(
                seamCurveData,
                (seamCurveData) => {
                    if (seamCurveData.length === 0) return null
                    return true
                },
                "hasSeamCurves",
            ),
        )

        this.setupObject(
            scope,
            context,
            "Seam",
            undefined,
            undefined,
            (objectProps) => {
                return {
                    displayList: scope.pureLambda(
                        scope.tuple(seamCurveData, objectProps.$id, objectProps.id, objectProps.path, meshes, bridgeMeshes),
                        ([seamCurveData, $id, id, path, meshes, bridgeMeshes]) => {
                            const seamCurves = seamCurveData.map((curve) => curve.seamCurve)
                            const bridgeCurves = seamCurveData.map((curve) => curve.bridgeCurve).filter((curve): curve is SeamCurve => curve !== null)

                            return [
                                ...seamCurves.map<SceneNodes.Seam>(({id: curveId, completeCurveData, meshCurveControl, transform}) => ({
                                    type: "Seam",
                                    id: `${id}_${curveId}`,
                                    $id: `${$id}_${curveId}`,
                                    path,
                                    item: meshes,
                                    completeCurveData,
                                    meshCurveControl,
                                    transform,
                                })),
                                ...bridgeCurves.map<SceneNodes.Seam>(({id: curveId, completeCurveData, meshCurveControl, transform}) => ({
                                    type: "Seam",
                                    id: `${id}_bridge_${curveId}`,
                                    $id: `${$id}_bridge_${curveId}`,
                                    path,
                                    item: bridgeMeshes,
                                    completeCurveData,
                                    meshCurveControl,
                                    transform,
                                })),
                            ]
                        },
                        "Seam",
                    ),
                    preDisplayList: scope.pureLambda(
                        scope.tuple(
                            scope.sparseMerge([preDisplayList, bridgePreDisplayList, offsetPreDisplayList, segmentPreDisplayList], "mergePreDisplayLists"),
                            objectProps.path,
                            scope,
                        ),
                        ([preDisplayList, path, scope]) => {
                            return preDisplayList.map((x) => {
                                if (SceneNodes.DataObjectReference.is(x)) {
                                    const finalId = x.id.split("@").pop()
                                    if (!finalId) throw new Error("Invalid DataObjectReference id")
                                    return {...x, id: `${scope.getPrefix()}:DataObjectReference@${finalId}`, path}
                                }
                                return x
                            })
                        },
                        "mappedPreDisplayList",
                    ),
                }
            },
            scope.phi(objectDataInvalid, hasNoSeamCurves),
        )

        return visitNone(this.parameters)
    }
}
